import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { CheckCircle2, FileText } from "lucide-react";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/legal/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const sign = async (e) => {
    e.preventDefault();
    if (!ack) return toast.error("Please acknowledge the document.");
    if (!name.trim()) return toast.error("Enter your full legal name.");
    setBusy(true);
    try {
      await api.post("/legal/sign", { doc_id: active.id, full_name: name, ack });
      toast.success("Document signed.");
      setActive(null); setAck(false); setName("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="text-3xl mb-2">Legal Documents</h1>
      <p className="text-sm text-muted-foreground mb-8">Review and e-sign your fund documents. Signatures are timestamped and recorded to the audit trail.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {docs.map((d) => (
          <div key={d.id} data-testid={`doc-${d.id}`} className="surface p-6 flex flex-col">
            <div className="flex items-start gap-3">
              <FileText size={20} strokeWidth={1.5} className="gold mt-1" />
              <div className="flex-1">
                <div className="kicker mb-1">v{d.version} · {d.kind.toUpperCase()}</div>
                <h3 className="text-lg mb-3">{d.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{d.body}</p>
              </div>
            </div>
            {d.signature ? (
              <div className="mt-auto flex items-center gap-2 text-sm pos">
                <CheckCircle2 size={16} /> Signed by {d.signature.full_name} · {fmtDateTime(d.signature.signed_at)}
              </div>
            ) : (
              <button data-testid={`sign-open-${d.id}`} onClick={() => setActive(d)}
                className="btn-gold mt-auto px-4 py-2 text-xs uppercase tracking-widest self-start">Review & Sign</button>
            )}
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="surface max-w-2xl w-full p-8">
            <div className="kicker mb-2">v{active.version}</div>
            <h2 className="text-2xl mb-4">{active.title}</h2>
            <div className="text-sm text-muted-foreground leading-relaxed border border-border p-4 max-h-64 overflow-auto mb-6">
              {active.body}
            </div>
            <form onSubmit={sign} className="space-y-4">
              <label className="block"><span className="kicker text-[10px] block mb-2">Type your full legal name</span>
                <input required data-testid="sign-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" /></label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input data-testid="sign-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
                <span>I have read and agree to the terms of this document, and my typed name constitutes my legal signature.</span>
              </label>
              <div className="flex gap-2">
                <button data-testid="sign-submit" disabled={busy} type="submit" className="btn-gold px-5 py-2 text-xs uppercase tracking-widest">{busy ? "Signing…" : "Sign Document"}</button>
                <button type="button" onClick={() => setActive(null)} className="border border-border px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
